import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GeneralUserService } from '@coin/shared/auth/data-access';
import {
  LoadLoggedInUser,
  LoadLoggedInUserSuccess,
  LoadUser,
  LoadUserSuccess,
  ResetUser,
  SetDynamicMercerAccess
} from '@coin/shared/auth/util';
import { PermissionResource } from '@coin/shared/core/util-enums';
import { CoinUser, Employee, MergedPermission } from '@coin/shared/core/util-models';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

export interface UserStateModel {
  user: CoinUser; // can be the emulated user - otherwise identical with loggedInUser
  loggedInUser: CoinUser; // static - always the real user
  token: string;
  directs: Employee[];
  isDynamicMercerAccessAllowed: boolean;
  accessViews: PermissionResource[]; // always based on user (not loggedInUser)
}

@State<UserStateModel>({
  name: 'userState',
  defaults: {
    token: '',
    user: null,
    loggedInUser: null,
    directs: [],
    isDynamicMercerAccessAllowed: false,
    accessViews: undefined
  }
})
@Injectable({ providedIn: 'root' })
export class UserState {
  private userService = inject(GeneralUserService);
  private store = inject(Store);

  public currentUserDetails$ = this.store.select(UserState.user).pipe(
    filter(user => !!user),
    switchMap(user =>
      this.userService.getEmployeeDetailsByGid(user.gid).pipe(catchError(() => of(null)))
    ),
    shareReplay({ bufferSize: 1, refCount: false })
  );
  public currentUserDetails = toSignal(this.currentUserDetails$);

  @Selector()
  static user(state: UserStateModel): CoinUser {
    return state.user;
  }

  @Selector()
  static loggedInUser(state: UserStateModel): CoinUser {
    return state.loggedInUser;
  }

  @Selector()
  static token(state: UserStateModel): string {
    return state.token;
  }

  @Selector()
  static directs(state: UserStateModel): Employee[] {
    return state.directs;
  }

  @Selector()
  static accessViews(state: UserStateModel): Set<PermissionResource> {
    return state.accessViews?.reduce<Set<PermissionResource>>((set, resource) => {
      set.add(resource);
      return set;
    }, new Set());
  }

  @Selector()
  static allPermissions(state: UserStateModel): Map<PermissionResource, MergedPermission> {
    const allRolePermissions = state.user.roles.flatMap(role => role.permissions);
    return allRolePermissions.reduce<Map<PermissionResource, MergedPermission>>((map, permission) => {
      if (!map.has(permission.resource)) {
        map.set(permission.resource, { resource: permission.resource, httpActions: new Set([permission.httpAction]) });
      } else {
        map.get(permission.resource).httpActions.add(permission.httpAction);
      }
      return map;
    }, new Map());
  }

  @Selector()
  static isDynamicMercerAccessAllowed(state: UserStateModel): boolean {
    return state.isDynamicMercerAccessAllowed;
  }

  @Action(LoadUser)
  loadUser(ctx: StateContext<UserStateModel>, { useAsLoggedInUser }: LoadUser): Observable<void> {
    if (ctx.getState().user?.gid) {
      return of(null);
    }

    return this.userService
      .getUserByToken()
      .pipe(switchMap(user => ctx.dispatch(new LoadUserSuccess(user, useAsLoggedInUser))));
  }

  @Action(LoadLoggedInUser)
  loadLoggedInUser(ctx: StateContext<UserStateModel>): Observable<void> {
    if (ctx.getState().loggedInUser?.gid) {
      return of(null);
    }

    return this.userService
      .getUserByToken(true)
      .pipe(switchMap(user => ctx.dispatch(new LoadLoggedInUserSuccess(user))));
  }

  @Action(LoadUserSuccess)
  loadUserSuccess({ patchState }: StateContext<UserStateModel>, { payload, useAsLoggedInUser }: LoadUserSuccess): void {
    patchState({ token: payload.token });
    patchState({ user: payload.principal });
    patchState({ accessViews: payload.accessViews });

    if (useAsLoggedInUser) {
      patchState({ loggedInUser: payload.principal });
    }
  }

  @Action(LoadLoggedInUserSuccess)
  loadLoggedInUserSuccess({ patchState }: StateContext<UserStateModel>, { payload }: LoadLoggedInUserSuccess): void {
    patchState({ loggedInUser: payload.principal });
  }

  @Action(SetDynamicMercerAccess)
  setDynamicMercerAccess({ patchState }: StateContext<UserStateModel>, { hasAccess }: SetDynamicMercerAccess): void {
    patchState({ isDynamicMercerAccessAllowed: hasAccess });
  }

  @Action(ResetUser)
  resetUser(ctx: StateContext<UserStateModel>): void {
    ctx.setState({
      token: '',
      user: null,
      loggedInUser: null,
      directs: [],
      isDynamicMercerAccessAllowed: false,
      accessViews: []
    });
  }
}
